/**
 * 将素材 creative 上的空串/非法绝对 URL 归一为 null，避免 ZodURL 抛 TypeError
 * 导致整个 creative-assets 列表契约校验失败。
 *
 * Callers: tiktokCreativeAssetsQueryOptions（apiClient schema.parse 前）
 */

const CREATIVE_URL_KEYS = [
  "accountProfileImageUrl",
  "coverUrl",
  "postUrl",
  "previewUrl",
  "videoCdnUrl",
] as const;

function coerceNullableHttpUrl(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return trimmed;
  } catch {
    return null;
  }
}

/** 深拷贝并归一 `data.creatives.items[].creative` 的可空 URL 字段。 */
export function normalizeCreativeAssetsDataUrls(data: unknown): unknown {
  if (!data || typeof data !== "object") return data;
  const root = data as Record<string, unknown>;
  const creatives = root.creatives;
  if (!creatives || typeof creatives !== "object") return data;
  const page = creatives as Record<string, unknown>;
  const items = page.items;
  if (!Array.isArray(items)) return data;

  return {
    ...root,
    creatives: {
      ...page,
      items: items.map((item) => {
        if (!item || typeof item !== "object") return item;
        const row = item as Record<string, unknown>;
        const creative = row.creative;
        if (!creative || typeof creative !== "object") return item;
        const nextCreative = { ...(creative as Record<string, unknown>) };
        for (const key of CREATIVE_URL_KEYS) {
          if (key in nextCreative) {
            nextCreative[key] = coerceNullableHttpUrl(nextCreative[key]);
          }
        }
        return { ...row, creative: nextCreative };
      }),
    },
  };
}

/** apiSuccess envelope：只改 data，保留 requestId 等。 */
export function normalizeCreativeAssetsSuccessPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== "object") return payload;
  const envelope = payload as Record<string, unknown>;
  if (!("data" in envelope)) return payload;
  return {
    ...envelope,
    data: normalizeCreativeAssetsDataUrls(envelope.data),
  };
}
